import { Box, Card, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PHASES, STAGE_OWNER, stageByNo } from '../constants/stages';
import { RT } from '../theme/retentionTheme';
import StageAuditFooter from './StageAuditFooter';
import StageActions from './StageActions';
import { IconBadge, SoftChip } from './ui';

const OWNER_ICON = {
  CRO: 'solar:user-id-bold',
  Tutor: 'solar:square-academic-cap-bold',
  Parent: 'solar:users-group-rounded-bold',
  System: 'solar:settings-bold',
  Mixed: 'solar:users-group-two-rounded-bold',
};

// The frame every stage form sits in. `currentStage` (from useRetention) drives the state styling:
// done → green tick, current → phase-coloured ring, upcoming → muted.
export default function StageCard({ stageNo, currentStage, progress, actions, children }) {
  const stage = stageByNo(stageNo);
  const phase = PHASES[stage?.phase || 1];
  const status = progress?.stage_status;
  const done = status === 2 || (status === undefined && currentStage && stageNo < currentStage);
  const current = stageNo === currentStage;
  const owner = STAGE_OWNER[stage?.owner];

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderColor: current ? alpha(phase.color, 0.45) : RT.line,
        boxShadow: current ? `0 0 0 3px ${alpha(phase.color, 0.12)}, ${RT.shadow}` : RT.shadow,
        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: phase.gradient },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.75} sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}>
        <IconBadge icon={done ? 'solar:check-circle-bold' : stage?.icon} color={done ? RT.success : phase.color} size={44} solid={current} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: phase.color, lineHeight: 1.4 }}>
            Stage {stage?.no}
            {current ? ' · now' : ''}
          </Typography>
          <Typography variant="h6" noWrap>
            {stage?.short}
          </Typography>
        </Box>
        {owner && <SoftChip label={owner} icon={OWNER_ICON[owner]} color={RT.ink2} sx={{ bgcolor: '#F1F3F8' }} />}
      </Stack>

      <Box sx={{ px: 2.5, pb: 2.25, opacity: !done && !current ? 0.85 : 1 }}>{children}</Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1.5}
        flexWrap="wrap"
        sx={{ px: 2.5, py: 1.25, bgcolor: '#FAFBFD', borderTop: `1px solid ${RT.line}` }}
      >
        <StageAuditFooter progress={progress} />
        <Stack direction="row" gap={1}>
          {actions}
          <StageActions stageNo={stageNo} progress={progress} />
        </Stack>
      </Stack>
    </Card>
  );
}
